import { useQuery } from '@apollo/client';
import type { FetchFlagGlossaryQuery } from '@generated/graphql';
import { UserContext } from '@providers/userContextProvider';
import type { Theme } from 'hephaestus/core';
import { useSnackbar } from 'hephaestus/web';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlagGlossaryKpiSummaryProps } from '../dashboard/flagGlossaryKpiSummary';
import type { FlagGlossaryFiltersCardProps } from '../filters/flagGlossaryFiltersCard.types';
import { FETCH_FLAG_GLOSSARY } from '../flagGlossary.queries';
import type { FlagGlossaryGridCardProps } from '../table/flagGlossaryGridCard';
import { useFlagGlossaryGrid } from './useFlagGlossaryGrid';
import { useFlagGlossaryView } from './useFlagGlossaryView';

const EMPTY_GLOSSARY_ENTRIES: FetchFlagGlossaryQuery['fetchFlagGlossary'] = [];

export type UseFlagGlossaryPageResult = {
  t: ReturnType<typeof useTranslation<'reconciliation'>>[0];
  theme: Theme;
  orgId: string | undefined;
  kpiProps: FlagGlossaryKpiSummaryProps;
  filtersProps: Omit<FlagGlossaryFiltersCardProps, 'theme'>;
  gridProps: FlagGlossaryGridCardProps;
};

export const useFlagGlossaryPage = (
  theme: Theme,
): UseFlagGlossaryPageResult => {
  const { t } = useTranslation(['reconciliation']);
  const { enqueueSnackbar } = useSnackbar();
  const { activeOrganization } = useContext(UserContext);
  const orgId = activeOrganization?.id;

  const { loading, data, error } = useQuery<FetchFlagGlossaryQuery>(
    FETCH_FLAG_GLOSSARY,
    {
      variables: { organizationId: orgId ?? '' },
      skip: !orgId,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (!error) return;
    enqueueSnackbar(t('flagGlossary.loadError'), { variant: 'error' });
  }, [error, enqueueSnackbar, t]);

  const { filters, filtersProps, filteredRows, kpiCounts } =
    useFlagGlossaryView({
      entries: data?.fetchFlagGlossary ?? EMPTY_GLOSSARY_ENTRIES,
      t,
    });

  const { gridProps } = useFlagGlossaryGrid({
    theme,
    t,
    rows: filteredRows,
    filters,
    loading,
  });

  const hasNoData = !data;
  const initialLoading = loading && hasNoData;
  const hasLoadError = Boolean(error) && hasNoData;

  return {
    t,
    theme,
    orgId,
    kpiProps: { kpiCounts, loading: initialLoading || hasLoadError },
    filtersProps,
    gridProps: {
      ...gridProps,
      hasActiveFilters: filtersProps.hasActiveFilters,
      hasError: hasLoadError,
    },
  };
};
